import Route from '@ember/routing/route';
import ApplicationController from '../controllers/application';
import { service } from '@ember/service';
import type MetaService from 'marcemira/services/meta';
import type RouterService from '@ember/routing/router-service';
import {
  generatePersonSchema,
  generateProfilePageSchema,
  generateBreadcrumbSchema,
  generateOrganizationSchema
} from 'marcemira/utils/schema-org';

export default class AboutRoute extends Route {
  @service('intl') declare intl: any;
  @service declare meta: MetaService;
  @service declare router: RouterService;

  model() {
    const technologies = [
      'EmberJS', 'React',
      'TypeScript', 'Node.js',
      'CSS', 'SCSS', 'Tailwind',
      'PostgreSQL', 'MySQL',
      'GraphQL', 'JSON:API',
      'Kurier.js', 'Nest.js',
      'Github', 'Gitlab',
      'Canva', 'Figma', 'Excalidraw',
      'Blender', 'Unreal Engine',
      'DigitalOcean', 'AWS', 'OCI',
      'Cloudflare',
      'Docker', 'Portainer', 'Kubernetes',
    ];

    const knowledges = [
      {
        labelKey: 'about.knowledges-langOne',
        valueKey: 'about.knowledges-langOne-val',
      },
      {
        labelKey: 'about.knowledges-langTwo',
        valueKey: 'about.knowledges-langTwo-val',
      },
      {
        labelKey: 'about.knowledges-design',
        valueKey: 'about.knowledges-design-val',
      },
      {
        labelKey: 'about.knowledges-gameDev',
        valueKey: 'about.knowledges-gameDev-val',
      },
      {
        labelKey: 'about.knowledges-devops',
        valueKey: 'about.knowledges-devops-val',
      },
    ];

    const workExperiences = [
      {
        company: 'Madow Tech',
        companyKey: 'about.px-madow',
        startDate: '2024',
        rolesKey: 'about.px-roles-madow',
        descriptionKey: 'about.px-madow-desc',
      },
      {
        company: 'Feldico',
        companyKey: 'about.px-feldico',
        startDate: 'Dec 2021',
        rolesKey: 'about.px-roles-feldico',
        descriptionKey: 'about.px-feldico-desc',
      },
      {
        company: 'ID Plans',
        startDate: '04/2020',
        roles: 'Senior Ember.js Developer',
        descriptionKey: 'about.px-idplans-desc',
      },
      {
        company: 'Ember consultant',
        startDate: '06/2020',
        endDate: '04/2021',
        roles: 'Ember Octane app conversion / Consultant',
        description:
          'Helped ScreenSteps and CloudTomatoes on their Octane apps conversions',
      },
      {
        company: 'Bagaar',
        startDate: '03/2020',
        endDate: '07/2020',
        rolesKey: 'about.px-roles-bagaar',
        descriptionKey: 'about.px-ba-desc',
      },
      {
        company: 'Outdoorsy',
        startDate: '12/2016',
        endDate: '03/2020',
        rolesKey: 'about.px-roles-outdoorsy',
        descriptionKey: 'about.px-ou-desc',
      },
      {
        company: 'Diezpe/Lancers',
        startDate: '07/2013',
        endDate: '06/2016',
        rolesKey: 'about.px-roles-diezpe',
        descriptionKey: 'about.px-dp',
      },
      {
        company: 'Lunchio.de',
        companyKey: 'about.px-lun',
        startDate: '05/2016',
        endDate: '09/2016',
        roles: 'Senior Developer JS/PHP',
        descriptionKey: 'about.px-lun-desc',
      },
      {
        company: 'Machinalis',
        companyKey: 'about.px-mac',
        startDate: '07/2015',
        endDate: '02/2016',
        roles: 'Senior Front-end/JS',
        descriptionKey: 'about.px-mac-desc',
      },
      {
        company: 'Government of the Province of Córdoba',
        companyKey: 'about.px-gov',
        startDate: '11/2013',
        endDate: '06/2015',
        roles: 'Senior Front-end/JS/PHP/C#',
        descriptionKey: 'about.px-gov-desc',
      },
      {
        company: 'Pixel S.A.',
        startDate: '07/2013',
        endDate: '11/2013',
        roles: 'Semi-Senior Front-end/JS/PHP',
        descriptionKey: 'about.px-pixel',
      },
      {
        company: 'Moobin Web Design',
        startDate: '10/2011',
        endDate: '07/2013',
        rolesKey: 'about.px-roles-moobin',
        descriptionKey: 'about.px-moobin',
      },
      {
        company: 'Pedeolmos S.R.L.',
        startDate: '05/2011',
        endDate: '12/2011',
        roles: 'Semi-Senior PHP developer, Front-end',
        descriptionKey: 'about.px-pede',
      },
      {
        company: 'Biobytes Software',
        startDate: '01/2010',
        endDate: '05/2011',
        rolesKey: 'about.px-roles-biobytes',
        descriptionKey: 'about.px-bio',
      },
      {
        company: 'M2 Game Design',
        startDate: '01/2006',
        endDate: '01/2010',
        rolesKey: 'about.px-roles-m2',
        descriptionKey: 'about.px-m2',
      },
    ];

    const skills = [
      {
        title: 'Ember.js',
        descriptionKey: 'about.sk-ember',
        images: [
          {
            src: '/img/about/js.svg',
            alt: 'JavaScript',
            class: 'w-[80px] left-[75px] top-[11px]',
          },
          {
            src: '/img/about/ember.svg',
            alt: 'Ember.js',
            class: 'left-[5px] w-[80px] top-[11px]',
          },
        ],
      },
      {
        title: 'Symfony + PHP7',
        descriptionKey: 'about.sk-symfony',
        images: [
          {
            src: '/img/about/symfony.svg',
            alt: 'Symfony',
            class: 'w-[95px] left-0 -mt-2 top-[30px]',
          },
          {
            src: '/img/about/php.svg',
            alt: 'PHP',
            class: 'w-[85px] left-[85px] top-[38px]',
          },
        ],
      },
      {
        title: 'UI/UX Design',
        descriptionKey: 'about.sk-html5',
        images: [
          {
            src: '/img/about/hmcss.svg',
            alt: 'HTML/CSS',
            class: 'h-[80px] left-0 top-[20px]',
          },
          {
            src: '/img/about/sass.svg',
            alt: 'Sass',
            class: 'h-[55px] left-[111px] top-[30px]',
          },
        ],
      },
    ];

    return {
      technologies,
      knowledges,
      workExperiences,
      skills,
    };
  }

  activate(transition: any) {
    super.activate(transition);
    const appController = this.controllerFor(
      'application'
    ) as ApplicationController;
    appController.iconFlipped = false;
  }

  afterModel() {
    // Set up SEO metadata
    const currentURL = `https://marcemira.com${this.router.currentURL}`;

    this.meta.title = 'About Me - Marcelo Mira';
    this.meta.description = "Remote full-stack developer with 26+ years of experience. Ember.js expert, TypeScript specialist, CTO-level consultant. Specializing in frontend architecture, Node.js backends, UX/UI design, and agile team leadership.";
    this.meta.canonicalUrl = currentURL;

    // Set alternate language URLs (hreflang)
    this.meta.alternateLanguages = [
      { lang: 'en', url: currentURL },
      { lang: 'es', url: currentURL }
    ];

    // Set additional meta tags
    this.meta.setTags([
      {
        name: 'keywords',
        content: 'Marcelo Mira, Mars, Remote Full-Stack Developer, Ember.js Expert, TypeScript Specialist, Node.js Developer, NestJS, CTO Consultant, Frontend Architecture, UX/UI Designer, Agile Leadership, Software Contractor, Product Development, Startup Consulting, Córdoba Argentina, Feldico Founder'
      },
      { property: 'og:type', content: 'profile' },
      { property: 'og:url', content: currentURL },
      { property: 'og:image', content: 'https://marcemira.com/img/marcelo_mira_3.jpg' },
      { property: 'og:image:width', content: '1920' },
      { property: 'og:image:height', content: '1080' },
      { property: 'og:image:alt', content: 'Marcelo Mira - Remote Full-Stack Developer & Ember.js Expert' },
    ]);

    // Generate Person schema with work experience
    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': 'Marcelo Ariel Mira Marin',
      'givenName': 'Marcelo',
      'familyName': 'Mira Marin',
      'alternateName': 'Mars',
      'jobTitle': 'Remote Full-Stack Web Developer, Ember.js Expert & CTO Consultant',
      'description': "Self-taught remote full-stack developer and UX/UI designer with 26+ years of experience. Ember.js expert and TypeScript specialist building rich, user-friendly applications. CTO-level consultant for startups, specializing in frontend architecture, Node.js backends with NestJS, agile team leadership, and product development from concept to production.",
      'url': currentURL,
      'image': 'https://marcemira.com/img/marcelo_mira_3.jpg',
      'email': 'hello@marcemira.com',
      'sameAs': [
        'https://www.linkedin.com/in/marcemira',
        'https://github.com/marcemira'
      ],
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Córdoba',
        'addressCountry': 'AR'
      },
      'birthDate': '1986-12-05',
      'nationality': 'Argentina',
      'knowsLanguage': [
        {
          '@type': 'Language',
          'name': 'Spanish',
          'alternateName': 'es'
        },
        {
          '@type': 'Language',
          'name': 'English',
          'alternateName': 'en'
        }
      ],
      'knowsAbout': [
        'Ember.js', 'TypeScript', 'JavaScript', 'Node.js', 'NestJS',
        'React', 'HTML5', 'CSS3', 'Tailwind CSS', 'UX/UI Design',
        'PostgreSQL', 'MySQL', 'GraphQL', 'REST API', 'FastBoot',
        'Docker', 'AWS', 'DigitalOcean', 'Git', 'Agile Development',
        'Frontend Architecture', 'CTO Consulting', 'Product Development',
        'Team Leadership', 'Continuous Learning', 'Software Contracting'
      ],
      'hasOccupation': [
        {
          '@type': 'Occupation',
          'name': 'Founder, CEO, CTO',
          'occupationLocation': {
            '@type': 'City',
            'name': 'Córdoba, Argentina'
          },
          'mainEntityOfPage': {
            '@type': 'Organization',
            'name': 'Feldico',
            'url': 'https://feldico.com'
          },
          'startDate': '2021-12'
        },
        {
          '@type': 'Occupation',
          'name': 'Founder, Lead Developer',
          'occupationLocation': {
            '@type': 'City',
            'name': 'Córdoba, Argentina'
          },
          'mainEntityOfPage': {
            '@type': 'Organization',
            'name': 'Madow Tech'
          },
          'startDate': '2024-01'
        },
        {
          '@type': 'Occupation',
          'name': 'Senior Ember.js Developer',
          'occupationLocation': {
            '@type': 'City',
            'name': 'Remote'
          },
          'mainEntityOfPage': {
            '@type': 'Organization',
            'name': 'ID Plans'
          },
          'startDate': '2020-04'
        }
      ]
    };

    // Generate Profile Page schema
    const profileSchema = generateProfilePageSchema({
      name: 'Marcelo Mira - About Me',
      description: "Professional profile and work experience of Marcelo Mira, a full-stack developer with expertise in Ember.js, TypeScript, and UX/UI design.",
      url: currentURL,
      image: 'https://marcemira.com/img/marcelo_mira_3.jpg',
      skills: [
        'Ember.js', 'TypeScript', 'Node.js', 'NestJS', 'React',
        'Tailwind CSS', 'UX/UI Design', 'PostgreSQL', 'GraphQL',
        'Docker', 'AWS', 'Fastboot'
      ]
    });

    // Generate Breadcrumb schema
    const breadcrumbSchema = generateBreadcrumbSchema([
      { name: 'Home', url: 'https://marcemira.com' },
      { name: 'About Me', url: currentURL }
    ]);

    // Generate Organization schemas for current companies
    const feldicoSchema = generateOrganizationSchema({
      name: 'Feldico',
      url: 'https://feldico.com',
      description: 'Revolutionizing farming in Argentina by connecting farmers with contractors',
      foundingDate: '2021-12',
      founder: 'Marcelo Mira',
      location: 'Córdoba, Argentina',
      sameAs: [
        'https://www.linkedin.com/company/feldico/',
        'https://www.instagram.com/feldicoapp/',
        'https://www.facebook.com/Feldico-111226938393389'
      ]
    });

    const madowSchema = generateOrganizationSchema({
      name: 'Madow Tech',
      description: 'A software factory focused on building innovative web solutions and digital products',
      foundingDate: '2024',
      founder: 'Marcelo Mira',
      location: 'Córdoba, Argentina'
    });

    // Add structured data
    this.meta.scriptTags = [
      {
        type: 'application/ld+json',
        id: 'structured-data-person',
        content: JSON.stringify(personSchema)
      },
      {
        type: 'application/ld+json',
        id: 'structured-data-profile',
        content: JSON.stringify(profileSchema)
      },
      {
        type: 'application/ld+json',
        id: 'structured-data-breadcrumb',
        content: JSON.stringify(breadcrumbSchema)
      },
      {
        type: 'application/ld+json',
        id: 'structured-data-feldico',
        content: JSON.stringify(feldicoSchema)
      },
      {
        type: 'application/ld+json',
        id: 'structured-data-madow',
        content: JSON.stringify(madowSchema)
      }
    ];

    // Render meta tags to header
    this.meta.renderMetaToHeader();
  }
}
